import m from "mithril";
import { getUserData, getMithril, postMithril, putMithril, deleteMithril, showToast, formatDateWIB, parseUTC } from "../utils.js";
import { showConfirmation } from "../js/toaster.js";

var SavingsDeposits = {
    oninit: function (vnode) {
        // Initialize component state
        vnode.state.savings = [];
        vnode.state.loading = false;
        vnode.state.error = null;
        vnode.state.showCreateForm = false;
        vnode.state.searchTerm = '';
        vnode.state.searchMode = 'account'; // 'account' or 'customer'
        vnode.state.customers = [];
        vnode.state.isSearching = false;

        // Customer autocomplete states
        vnode.state.customerSearchTerm = '';
        vnode.state.customerSuggestions = [];
        vnode.state.showCustomerSuggestions = false;
        vnode.state.selectedCustomer = null;
        vnode.state.isSearchingCustomers = false;

        // Initialize form data
        vnode.state.formData = {
            savingsAccountId: '',
            amount: '',
            formatted_amount: '',
            description: ''
        };
        vnode.state.formErrors = {};
        vnode.state.isSubmitting = false;

        // Don't fetch savings data initially - wait for search
    },

    fetchSavings: async function (vnode, searchParams = {}) {
        try {
            vnode.state.loading = true;

            // Build query parameters
            const params = new URLSearchParams(searchParams);
            const url = `/api/savings${params.toString() ? '?' + params.toString() : ''}`;

            const result = await getMithril(url, { component: vnode.state });

            if (result.success) {
                vnode.state.savings = result.data;
                vnode.state.error = null;
            } else {
                vnode.state.error = result.error || 'Gagal memuat data setoran simpanan';
            }
        } catch (error) {
            console.error('Error fetching savings:', error);
            vnode.state.error = 'Terjadi kesalahan saat memuat data';
        } finally {
            vnode.state.loading = false;
            m.redraw();
        }
    },

    searchSavings: async function (vnode) {
        if (!vnode.state.searchTerm.trim()) {
            vnode.state.error = 'Masukkan nomor rekening atau nama nasabah untuk mencari';
            m.redraw();
            return;
        }

        try {
            vnode.state.isSearching = true;
            vnode.state.error = null;

            const searchParams = {};
            if (vnode.state.searchMode === 'account') {
                searchParams.accountNumber = vnode.state.searchTerm.trim();
            } else {
                searchParams.customerName = vnode.state.searchTerm.trim();
            }

            await this.fetchSavings(vnode, searchParams);
        } catch (error) {
            console.error('Error searching savings:', error);
            vnode.state.error = 'Terjadi kesalahan saat mencari data';
        } finally {
            vnode.state.isSearching = false;
            m.redraw();
        }
    },

    clearSearch: function (vnode) {
        vnode.state.searchTerm = '';
        vnode.state.savings = [];
        vnode.state.error = null;
        m.redraw();
    },

    showCreateForm: function (vnode) {
        vnode.state.showCreateForm = true;
        vnode.state.isEditing = false;
        vnode.state.formData = {
            savingsAccountId: '',
            amount: '',
            formatted_amount: '',
            description: ''
        };
        vnode.state.formErrors = {};
        m.redraw();
    },

    hideCreateForm: function (vnode) {
        vnode.state.showCreateForm = false;
        vnode.state.isEditing = false;
        vnode.state.formData = {
            savingsAccountId: '',
            amount: '',
            formatted_amount: '',
            description: ''
        };
        vnode.state.formErrors = {};
        vnode.state.customerSearchTerm = '';
        vnode.state.customerSuggestions = [];
        vnode.state.selectedCustomer = null;
        m.redraw();
    },

    formatDate: function (dateString) {
        return formatDateWIB(dateString);
    },

    formatCurrency: function (amount) {
        if (!amount) return 'Rp0';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    },

    // Format currency to Indonesian locale
    formatCurrencyInput: function (value) {
        if (!value || value === '') return '';
        const numericValue = parseFloat(value.toString().replace(/[^\d]/g, ''));
        if (isNaN(numericValue)) return '';
        return numericValue.toLocaleString('id-ID');
    },

    // Parse formatted currency back to number
    parseCurrency: function (value) {
        if (!value || value === '') return '';
        return value.toString().replace(/[^\d]/g, '');
    },

    fetchCustomers: async function (vnode) {
        try {
            const result = await getMithril('/api/customers', { component: vnode.state });
            if (result.success) {
                vnode.state.customers = result.data;
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
    },

    fetchProducts: async function (vnode) {
        try {
            const result = await getMithril('/api/saving-products', { component: vnode.state });
            if (result.success) {
                vnode.state.products = result.data;
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    },

    searchCustomers: async function (vnode, searchTerm) {
        if (!searchTerm || searchTerm.length < 2) {
            vnode.state.customerSuggestions = [];
            vnode.state.showCustomerSuggestions = false;
            m.redraw();
            return;
        }

        try {
            vnode.state.isSearchingCustomers = true;
            const result = await getMithril(`/api/customers?search=${encodeURIComponent(searchTerm)}`, { component: vnode.state });
            if (result.success) {
                vnode.state.customerSuggestions = result.data.slice(0, 5); // Limit to 5 suggestions
                vnode.state.showCustomerSuggestions = vnode.state.customerSuggestions.length > 0;
            }
        } catch (error) {
            console.error('Error searching customers:', error);
        } finally {
            vnode.state.isSearchingCustomers = false;
            m.redraw();
        }
    },

    selectCustomer: function (vnode, customer) {
        const displayName = customer.customer_type === 'kelompok'
            ? `${customer.nama_nasabah} (Kelompok Usaha)`
            : customer.nama_nasabah;
        vnode.state.customerSearchTerm = displayName;
        vnode.state.selectedCustomer = customer;
        vnode.state.showCustomerSuggestions = false;
        // Here we would fetch the customer's savings accounts
        this.fetchCustomerSavingsAccounts(vnode, customer.id);
        m.redraw();
    },

    fetchCustomerSavingsAccounts: async function (vnode, customerId) {
        try {
            const result = await getMithril(`/api/savings-accounts?customerId=${customerId}`, { component: vnode.state });
            if (result.success) {
                vnode.state.customerSavingsAccounts = result.data;
            }
        } catch (error) {
            console.error('Error fetching customer savings accounts:', error);
        }
    },

    clearCustomerSelection: function (vnode) {
        vnode.state.customerSearchTerm = '';
        vnode.state.selectedCustomer = null;
        vnode.state.customerSuggestions = [];
        vnode.state.customerSavingsAccounts = [];
        vnode.state.formData.savingsAccountId = '';
        m.redraw();
    },

    validateForm: function (vnode) {
        const errors = {};
        const data = vnode.state.formData;

        if (!data.savingsAccountId) {
            errors.savingsAccountId = 'Rekening simpanan harus dipilih';
        }

        if (!data.amount || parseFloat(data.amount) <= 0) {
            errors.amount = 'Jumlah harus diisi dengan nilai lebih dari 0';
        }

        if (!data.description) {
            errors.description = 'Deskripsi harus diisi';
        }

        vnode.state.formErrors = errors;
        return Object.keys(errors).length === 0;
    },

    submitForm: async function (vnode) {
        if (!this.validateForm(vnode)) {
            m.redraw();
            return;
        }

        try {
            vnode.state.isSubmitting = true;

            const url = vnode.state.isEditing ? `/api/savings/${vnode.state.editingSaving.id}` : '/api/savings';
            const body = {
                savingsAccountId: vnode.state.formData.savingsAccountId,
                amount: vnode.state.formData.amount,
                description: vnode.state.formData.description
            };

            const result = await (vnode.state.isEditing
                ? putMithril(url, body, { component: vnode.state })
                : postMithril(url, body, { component: vnode.state }));

            if (result.success) {
                showToast(vnode.state.isEditing ? 'Setoran berhasil diperbarui' : 'Setoran berhasil disimpan', 'success');
                this.hideCreateForm(vnode);
                // Refresh the savings list
                await this.searchSavings(vnode);
            } else {
                vnode.state.formErrors.general = result.error || 'Terjadi kesalahan saat menyimpan data';
                showToast(result.error || 'Gagal menyimpan setoran', 'error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            vnode.state.formErrors.general = 'Terjadi kesalahan saat menyimpan data';
            showToast('Terjadi kesalahan saat menyimpan data', 'error');
        } finally {
            vnode.state.isSubmitting = false;
            m.redraw();
        }
    },

    view: function (vnode) {
        return [
            m("div", { class: "max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6" }, [
                m("div", { class: "mb-6 flex flex-col md:flex-row md:items-center md:justify-between" }, [
                    m("div", { class: "mb-4 md:mb-0" }, [
                        m("h1", { class: "text-2xl font-bold text-gray-900 dark:text-white" }, "Setoran Simpanan"),
                        m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Kelola setoran simpanan nasabah")
                    ]),
                    m("div", [
                        m("button", {
                            class: "bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors duration-200",
                            onclick: () => vnode.tag.showCreateForm(vnode)
                        }, [
                            m("i", { class: "ri-add-line mr-2" }),
                            "Tambah Setoran"
                        ])
                    ])
                ]),

                // Search Section
                m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6" }, [
                    m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-4" }, "Cari Rekening Simpanan"),
                    m("div", { class: "flex flex-col md:flex-row gap-4" }, [
                        // Search mode selector
                        m("div", { class: "flex items-center" }, [
                            m("label", { class: "mr-4 text-gray-700 dark:text-gray-300" }, "Cari berdasarkan:"),
                            m("div", { class: "flex" }, [
                                m("label", { class: "mr-4 flex items-center" }, [
                                    m("input", {
                                        type: "radio",
                                        name: "searchMode",
                                        value: "account",
                                        checked: vnode.state.searchMode === "account",
                                        onchange: (e) => vnode.state.searchMode = e.target.value
                                    }),
                                    m("span", { class: "ml-2 text-gray-700 dark:text-gray-300" }, "Nomor Rekening")
                                ]),
                                m("label", { class: "flex items-center" }, [
                                    m("input", {
                                        type: "radio",
                                        name: "searchMode",
                                        value: "customer",
                                        checked: vnode.state.searchMode === "customer",
                                        onchange: (e) => vnode.state.searchMode = e.target.value
                                    }),
                                    m("span", { class: "ml-2 text-gray-700 dark:text-gray-300" }, "Nama Nasabah")
                                ])
                            ])
                        ]),

                        // Search input
                        m("div", { class: "flex-1" }, [
                            m("input", {
                                class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline",
                                type: "text",
                                placeholder: vnode.state.searchMode === "account" ? "Masukkan nomor rekening..." : "Masukkan nama nasabah...",
                                value: vnode.state.searchTerm,
                                oninput: (e) => vnode.state.searchTerm = e.target.value,
                                onkeypress: (e) => {
                                    if (e.key === 'Enter') {
                                        this.searchSavings(vnode);
                                    }
                                }
                            })
                        ]),

                        // Search button
                        m("button", {
                            class: "bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg flex items-center transition-colors duration-200",
                            onclick: () => this.searchSavings(vnode),
                            disabled: vnode.state.isSearching || !vnode.state.searchTerm.trim()
                        }, [
                            vnode.state.isSearching ? [
                                m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                                "Mencari..."
                            ] : [
                                m("i", { class: "ri-search-line mr-2" }),
                                "Cari"
                            ]
                        ]),

                        // Clear button
                        vnode.state.savings.length > 0 || vnode.state.searchTerm ? m("button", {
                            class: "bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center transition-colors duration-200",
                            onclick: () => this.clearSearch(vnode)
                        }, [
                            m("i", { class: "ri-close-line mr-2" }),
                            "Bersihkan"
                        ]) : null
                    ]),

                    // Search instructions
                    m("div", { class: "mt-4 text-sm text-gray-600 dark:text-gray-400" }, [
                        m("p", "Pilih mode pencarian, masukkan nomor rekening atau nama nasabah, lalu klik 'Cari' untuk melihat transaksi simpanan.")
                    ])
                ]),

                // Create Form Modal
                vnode.state.showCreateForm ? this.renderCreateForm(vnode) : null,

                vnode.state.loading ?
                    m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 text-center" }, [
                        m("div", { class: "flex justify-center items-center my-8" }, [
                            m("i", { class: "ri-loader-4-line animate-spin text-2xl text-orange-600" })
                        ]),
                        m("p", { class: "text-gray-700 dark:text-gray-300 mt-4" }, "Memuat data setoran simpanan...")
                    ]) :
                    vnode.state.error ?
                        m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 text-center" }, [
                            m("p", { class: "text-red-500 dark:text-red-400" }, vnode.state.error),
                            m("button", {
                                class: "mt-4 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors duration-200",
                                onclick: () => this.fetchSavings(vnode)
                            }, "Coba Lagi")
                        ]) :
                        vnode.state.savings.length === 0 && !vnode.state.searchTerm ?
                            m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 text-center" }, [
                                m("div", { class: "mb-4" }, [
                                    m("i", { class: "ri-search-line text-4xl text-gray-400" })
                                ]),
                                m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-2" }, "Cari Rekening Simpanan"),
                                m("p", { class: "text-gray-700 dark:text-gray-300 mb-4" }, "Gunakan form pencarian di atas untuk mencari rekening simpanan berdasarkan nomor rekening atau nama nasabah."),
                                m("p", { class: "text-gray-500 dark:text-gray-400 text-sm" }, "Hasil pencarian akan ditampilkan di sini.")
                            ]) :
                            vnode.state.savings.length === 0 && vnode.state.searchTerm ?
                                m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 text-center" }, [
                                    m("div", { class: "mb-4" }, [
                                        m("i", { class: "ri-file-search-line text-4xl text-gray-400" })
                                    ]),
                                    m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-2" }, "Tidak Ada Data Ditemukan"),
                                    m("p", { class: "text-gray-700 dark:text-gray-300" },
                                        `Tidak ada rekening simpanan yang ditemukan untuk "${vnode.state.searchTerm}".`),
                                    m("p", { class: "text-gray-500 dark:text-gray-400 mt-2 text-sm" }, "Coba gunakan kata kunci pencarian yang berbeda."),
                                    m("button", {
                                        class: "mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200",
                                        onclick: () => this.showCreateForm(vnode)
                                    }, [
                                        m("i", { class: "ri-add-line mr-2" }),
                                        "Setoran Baru"
                                    ])
                                ]) :
                                m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden" }, [
                                    m("div", { class: "overflow-x-auto" }, [
                                        m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                                            m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                                                m("tr", [
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "No"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Tanggal"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "No. Transaksi"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Deskripsi"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nasabah"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Produk"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Dibuat Oleh"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Jumlah"),
                                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Aksi")
                                                ])
                                            ]),
                                            m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" },
                                                vnode.state.savings.map((saving, index) =>
                                                    m("tr", { class: index % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-gray-50 dark:bg-gray-750" }, [
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" }, index + 1),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" },
                                                            this.formatDate(saving.tgl)
                                                        ),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, saving.no || '-'),
                                                        m("td", { class: "px-6 py-4 text-sm text-gray-900 dark:text-white" }, saving.deskripsi || '-'),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, saving.customer_name || '-'),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, saving.product_name || '-'),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" },
                                                            saving.created_by_name || '-'),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" },
                                                            this.formatCurrency(saving.total)
                                                        ),
                                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, [
                                                            // Edit button - Orange styling
                                                            m("button", {
                                                                class: "text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 mr-3",
                                                                onclick: () => this.editSaving(vnode, saving),
                                                                title: "Edit setoran"
                                                            }, [
                                                                m("i", { class: "ri-edit-line" })
                                                            ]),
                                                            // Delete button - Red styling
                                                            m("button", {
                                                                class: "text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300",
                                                                onclick: () => this.deleteSaving(vnode, saving),
                                                                title: "Hapus setoran"
                                                            }, [
                                                                m("i", { class: "ri-delete-bin-line" })
                                                            ])
                                                        ])
                                                    ])
                                                )
                                            )
                                        ])
                                    ])
                                ])
            ])
        ];
    },

    renderCreateForm: function (vnode) {
        // Trigger data loading if form is shown but data not loaded yet
        if (vnode.state.showCreateForm && !vnode.state.customers && !vnode.state.products && !vnode.state.isLoadingFormData) {
            vnode.state.isLoadingFormData = true;
            this.fetchCustomers(vnode);
            this.fetchProducts(vnode).then(() => {
                vnode.state.isLoadingFormData = false;
                m.redraw();
            });
        }

        return m("div", { class: "fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" }, [
            m("div", { class: "relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800" }, [
                m("div", { class: "mt-3" }, [
                    m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-4" },
                        vnode.state.isEditing ? "Edit Setoran Simpanan" : "Entry Setoran Simpanan"),
                    m("form", {
                        onsubmit: (e) => {
                            e.preventDefault();
                            this.submitForm(vnode);
                        }
                    }, [
                        // General error message
                        vnode.state.formErrors.general ?
                            m("div", { class: "mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded" }, vnode.state.formErrors.general) : null,

                        // Customer selection with autocomplete
                        m("div", { class: "mb-4 relative" }, [
                            m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Nasabah"),
                            m("div", { class: "relative" }, [
                                m("input", {
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline pr-10" + (vnode.state.formErrors.customerId ? " border-red-500" : ""),
                                    type: "text",
                                    placeholder: "Ketik nama nasabah...",
                                    value: vnode.state.customerSearchTerm,
                                    oninput: (e) => {
                                        vnode.state.customerSearchTerm = e.target.value;
                                        vnode.state.formData.savingsAccountId = '';
                                        vnode.state.selectedCustomer = null;
                                        this.searchCustomers(vnode, e.target.value);
                                    },
                                    onfocus: () => {
                                        if (vnode.state.customerSuggestions.length > 0) {
                                            vnode.state.showCustomerSuggestions = true;
                                        }
                                    },
                                    onblur: () => {
                                        // Delay hiding to allow click on suggestions
                                        setTimeout(() => {
                                            vnode.state.showCustomerSuggestions = false;
                                            m.redraw();
                                        }, 150);
                                    }
                                }),
                                // Clear button
                                vnode.state.customerSearchTerm ? m("button", {
                                    class: "absolute right-2 top-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
                                    onclick: (e) => {
                                        e.preventDefault();
                                        this.clearCustomerSelection(vnode);
                                    }
                                }, m("i", { class: "ri-close-line" })) : null,
                                // Loading indicator
                                vnode.state.isSearchingCustomers ? m("div", {
                                    class: "absolute right-2 top-2"
                                }, m("i", { class: "ri-loader-4-line animate-spin text-gray-500" })) : null
                            ]),

                            // Customer suggestions dropdown
                            vnode.state.showCustomerSuggestions && vnode.state.customerSuggestions.length > 0 ?
                                m("div", {
                                    class: "absolute z-10 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-b shadow-lg max-h-48 overflow-y-auto"
                                },
                                    vnode.state.customerSuggestions.map(customer => {
                                        const displayName = customer.customer_type === 'kelompok'
                                            ? `${customer.nama_nasabah} (Kelompok Usaha)`
                                            : customer.nama_nasabah;
                                        return m("div", {
                                            class: "px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0",
                                            onclick: () => this.selectCustomer(vnode, customer)
                                        }, [
                                            m("div", { class: "font-medium text-gray-900 dark:text-white" }, displayName),
                                            m("div", { class: "text-sm text-gray-500 dark:text-gray-400" }, `No. Reg: ${customer.no_reg}`)
                                        ]);
                                    })
                                ) : null,

                            vnode.state.formErrors.customerId ?
                                m("p", { class: "text-red-500 text-xs italic mt-1" }, vnode.state.formErrors.customerId) : null
                        ]),

                        // Account selection (based on selected customer)
                        vnode.state.selectedCustomer && vnode.state.customerSavingsAccounts ?
                            m("div", { class: "mb-4" }, [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Rekening Simpanan"),
                                m("select", {
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline" + (vnode.state.formErrors.savingsAccountId ? " border-red-500" : ""),
                                    value: vnode.state.formData.savingsAccountId,
                                    onchange: (e) => vnode.state.formData.savingsAccountId = e.target.value
                                }, [
                                    m("option", { value: "" }, "Pilih Rekening"),
                                    ...(vnode.state.customerSavingsAccounts || []).map(account =>
                                        m("option", { value: account.id }, `${account.account_number} - ${account.account_name}`)
                                    )
                                ]),
                                vnode.state.formErrors.savingsAccountId ?
                                    m("p", { class: "text-red-500 text-xs italic mt-1" }, vnode.state.formErrors.savingsAccountId) : null
                            ]) : null,

                        // Amount input
                        m("div", { class: "mb-4" }, [
                            m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Jumlah (Rp)"),
                            m("input", {
                                class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline" + (vnode.state.formErrors.amount ? " border-red-500" : ""),
                                type: "text",
                                placeholder: "Masukkan jumlah",
                                value: vnode.state.formData.formatted_amount || vnode.state.formData.amount,
                                oninput: (e) => {
                                    vnode.state.formData.amount = this.parseCurrency(e.target.value);
                                    vnode.state.formData.formatted_amount = this.formatCurrencyInput(e.target.value);
                                },
                                onblur: (e) => {
                                    const rawValue = this.parseCurrency(e.target.value);
                                    vnode.state.formData.amount = rawValue;
                                    vnode.state.formData.formatted_amount = this.formatCurrencyInput(rawValue);
                                    m.redraw();
                                }
                            }),
                            vnode.state.formErrors.amount ?
                                m("p", { class: "text-red-500 text-xs italic mt-1" }, vnode.state.formErrors.amount) : null
                        ]),

                        // Description input
                        m("div", { class: "mb-6" }, [
                            m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Deskripsi"),
                            m("textarea", {
                                class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 leading-tight focus:outline-none focus:shadow-outline" + (vnode.state.formErrors.description ? " border-red-500" : ""),
                                rows: "3",
                                placeholder: "Masukkan deskripsi setoran",
                                value: vnode.state.formData.description,
                                oninput: (e) => vnode.state.formData.description = e.target.value
                            }),
                            vnode.state.formErrors.description ?
                                m("p", { class: "text-red-500 text-xs italic mt-1" }, vnode.state.formErrors.description) : null
                        ]),

                        // Buttons
                        m("div", { class: "flex justify-end space-x-3" }, [
                            // Cancel button - Gray styling
                            m("button", {
                                class: "bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200",
                                type: "button",
                                onclick: () => this.hideCreateForm(vnode)
                            }, [
                                m("i", { class: "ri-close-line mr-2" }),
                                "Batal"
                            ]),
                            // Submit button with loading state
                            m("button", {
                                class: "bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors duration-200",
                                type: "submit",
                                disabled: vnode.state.isSubmitting
                            }, [
                                vnode.state.isSubmitting ? [
                                    m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                                    vnode.state.isEditing ? "Memperbarui..." : "Menyimpan..."
                                ] : [
                                    m("i", { class: "ri-check-line mr-2" }),
                                    vnode.state.isEditing ? "Perbarui" : "Simpan"
                                ]
                            ])
                        ])
                    ])
                ])
            ])
        ]);
    },

    editSaving: async function (vnode, saving) {
        try {
            // Fetch current data for editing
            vnode.state.editingSaving = saving;
            vnode.state.formData = {
                savingsAccountId: saving.saving_account_id?.toString() || '',
                amount: saving.total?.toString() || '',
                formatted_amount: saving.total ? this.formatCurrencyInput(saving.total) : '',
                description: saving.deskripsi || ''
            };
            vnode.state.formErrors = {};

            // Load form data if not already loaded
            if (!vnode.state.customers || !vnode.state.products) {
                vnode.state.isLoadingFormData = true;
                await Promise.all([
                    this.fetchCustomers(vnode),
                    this.fetchProducts(vnode)
                ]);
                vnode.state.isLoadingFormData = false;
            }

            vnode.state.showCreateForm = true;
            vnode.state.isEditing = true;
            m.redraw();
        } catch (error) {
            console.error('Error preparing edit form:', error);
            showToast('Gagal memuat data untuk edit', 'error');
        }
    },

    deleteSaving: async function (vnode, saving) {
        showConfirmation(`Apakah Anda yakin ingin menghapus setoran "${saving.deskripsi}" sebesar ${this.formatCurrency(saving.total)}?`, async () => {
            try {
                const result = await deleteMithril(`/api/savings/${saving.id}`, { component: vnode.state });

                if (result.success) {
                    showToast('Setoran simpanan berhasil dihapus', 'success');
                    // Refresh the savings list
                    await this.searchSavings(vnode);
                } else {
                    showToast(result.error || 'Gagal menghapus setoran simpanan', 'error');
                }
            } catch (error) {
                console.error('Error deleting saving:', error);
                showToast('Terjadi kesalahan saat menghapus setoran', 'error');
            }
        });
    }
};

export default SavingsDeposits;